import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Menu, X } from "lucide-react";

const linkCls = (color) => `text-xs uppercase tracking-wider ${color} hover:text-white transition-colors font-mono whitespace-nowrap`;

function UserLinks({ user, isDashboard, onNav, mobile = false }) {
  const base = mobile ? "block py-3 text-sm border-b border-zinc-900" : "";
  const items = [];
  if (user.is_admin || user.plan === "pro") {
    items.push(<Link key="at" to="/auto-trade" onClick={onNav} className={`${linkCls("text-[#F59E0B]")} ${base}`} data-testid="nav-auto-trade">Auto-Trade</Link>);
    items.push(<Link key="sc" to="/scalp-trade" onClick={onNav} className={`${linkCls("text-[#A855F7]")} ${base}`} data-testid="nav-scalp-trade">Scalp</Link>);
    items.push(<Link key="th" to="/theta" onClick={onNav} className={`${linkCls("text-[#3B82F6]")} ${base}`} data-testid="nav-theta">Theta</Link>);
  }
  if (user.is_admin) {
    items.push(<Link key="ql" to="/quant-lab" onClick={onNav} className={`${linkCls("text-[#22C55E]")} ${base}`} data-testid="nav-quant-lab">Quant Lab</Link>);
    items.push(<Link key="us" to="/admin/users" onClick={onNav} className={`${linkCls("text-[#3B9BFF]")} ${base}`} data-testid="nav-admin-users">Users</Link>);
  }
  if (!isDashboard || mobile) {
    items.push(<Link key="db" to="/dashboard" onClick={onNav} className={`text-sm uppercase tracking-wider text-white hover:text-[#007AFF] transition-colors whitespace-nowrap ${base}`} data-testid="nav-dashboard">Dashboard</Link>);
  }
  return <>{items}</>;
}

export default function Navbar({ variant = "marketing" }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isDashboard = variant === "dashboard";
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const doLogout = async () => { close(); await logout(); nav("/"); };

  return (
    <header
      className={
        isDashboard
          ? "border-b border-zinc-800 bg-[#0A0A0A] px-4 md:px-6 py-3 sticky top-0 z-40"
          : "fixed top-0 w-full z-50 backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/5 px-4 sm:px-6 md:px-12 py-4"
      }
    >
      <div className="flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo" onClick={close}>
          <img src="/brand/ptiq-icon.png" alt="ProTradeIQ" className="h-6 w-6 object-contain" />
          <span className="font-heading text-xl font-black tracking-tight uppercase">
            ProTrade<span className="text-[#007AFF]">IQ</span>
          </span>
        </Link>

        {/* desktop */}
        <nav className="hidden md:flex items-center gap-6">
          {!isDashboard && (
            <>
              <a href="#features" className="text-sm text-zinc-400 hover:text-white transition-colors uppercase tracking-wider" data-testid="nav-features">Features</a>
              <a href="#pricing" className="text-sm text-zinc-400 hover:text-white transition-colors uppercase tracking-wider" data-testid="nav-pricing">Pricing</a>
            </>
          )}
          {user ? (
            <div className="flex items-center gap-4">
              <UserLinks user={user} isDashboard={isDashboard} />
              <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-wider">
                <span className="px-2 py-1 border border-zinc-800 font-mono" data-testid="nav-plan">{user.plan}</span>
                <span className="hidden lg:inline" data-testid="nav-user-email">{user.email}</span>
              </div>
              <button onClick={doLogout} className="text-zinc-400 hover:text-white p-2" data-testid="nav-logout" aria-label="Log out">
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              <Link to="/login" className="text-sm uppercase tracking-wider text-zinc-300 hover:text-white" data-testid="nav-login">Sign in</Link>
              <Link to="/signup" className="bg-[#007AFF] text-white text-sm font-bold uppercase tracking-wider py-2 px-4 hover:bg-[#3395FF] transition-colors" data-testid="nav-signup">Start Free</Link>
            </>
          )}
        </nav>

        {/* mobile */}
        <div className="flex md:hidden items-center gap-2">
          {!user && (
            <Link to="/signup" className="bg-[#007AFF] text-white text-xs font-bold uppercase tracking-wider py-2 px-3" data-testid="nav-signup-mobile">Start Free</Link>
          )}
          <button onClick={() => setOpen((v) => !v)} className="p-2 text-zinc-300 hover:text-white" aria-label="Menu" aria-expanded={open} data-testid="nav-menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="md:hidden mt-3 border-t border-zinc-800 pt-2 animate-in fade-in slide-in-from-top-2 duration-200" data-testid="nav-mobile-menu">
          {user ? (
            <>
              <div className="flex items-center gap-2 py-2 text-[10px] text-zinc-500 uppercase tracking-wider font-mono">
                <span className="px-2 py-0.5 border border-zinc-800" data-testid="nav-plan-mobile">{user.plan}</span>
                <span className="truncate" data-testid="nav-user-email-mobile">{user.email}</span>
              </div>
              <UserLinks user={user} isDashboard={isDashboard} onNav={close} mobile />
              <button onClick={doLogout} className="w-full text-left py-3 text-sm uppercase tracking-wider text-zinc-400 hover:text-white flex items-center gap-2" data-testid="nav-logout-mobile">
                <LogOut className="h-4 w-4" /> Log out
              </button>
            </>
          ) : (
            <>
              {loc.pathname === "/" && (
                <>
                  <a href="#features" onClick={close} className="block py-3 text-sm text-zinc-300 uppercase tracking-wider border-b border-zinc-900" data-testid="nav-features-mobile">Features</a>
                  <a href="#pricing" onClick={close} className="block py-3 text-sm text-zinc-300 uppercase tracking-wider border-b border-zinc-900" data-testid="nav-pricing-mobile">Pricing</a>
                </>
              )}
              <Link to="/login" onClick={close} className="block py-3 text-sm text-zinc-300 uppercase tracking-wider" data-testid="nav-login-mobile">Sign in</Link>
            </>
          )}
        </nav>
      )}
    </header>
  );
}
